import React from "react";
import { Link } from "react-router-dom";
import { FilePlus2, ArrowRight, ShieldCheck, LogIn, LayoutTemplate, Download, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const FEATURES = [
  {
    icon: ShieldCheck,
    title: "100% Free",
    desc: "Create unlimited invoices completely free.",
  },
  {
    icon: LogIn,
    title: "No Login Required",
    desc: "No sign up or login. Start creating instantly.",
  },
  {
    icon: LayoutTemplate,
    title: "Professional Templates",
    desc: "Clean and professional invoice design.",
  },
  {
    icon: Download,
    title: "Download & Share",
    desc: "Download as PDF or share instantly.",
  },
];

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 ambient-glow" />
        <div className="absolute inset-0 grid-bg opacity-40" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Create Professional{" "}
              <br className="hidden sm:block" />
              Invoices <span className="text-blue-600">in Seconds</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-lg leading-relaxed">
              Easily generate, download and share beautiful invoices without login or sign up. <span className="font-semibold text-foreground">100% Free!</span>
            </p>

            <div className="mt-8">
              <Link to="/create">
                <Button
                  data-testid="hero-create-btn"
                  size="lg"
                  className="rounded-2xl bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/25 h-14 px-7 text-base"
                >
                  <FilePlus2 size={18} className="mr-2" /> Create Invoice Now <ArrowRight className="ml-3" size={16} />
                </Button>
              </Link>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-blue-600" /> No login required</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-blue-600" /> No data stored</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={16} className="text-blue-600" /> 100% Free</span>
            </div>
          </div>

          {/* Invoice mockup */}
          <div className="relative">
            {/* animated shapes */}
            <div className="absolute -inset-4 bg-blue-100/60 dark:bg-blue-950/30 rounded-[40%_60%_50%_50%/50%_40%_60%_50%] blur-2xl" />
            <div className="absolute top-6 -right-4 w-3 h-3 rounded-full bg-blue-500 animate-float" />
            <div className="absolute top-24 -left-6 w-2 h-2 rounded-full bg-blue-400 animate-float" style={{ animationDelay: "1s" }} />
            <div className="absolute bottom-16 right-8 w-2 h-2 rounded-full bg-blue-300 animate-float" style={{ animationDelay: "2s" }} />
            <div className="absolute bottom-2 -left-2 w-4 h-4 rotate-45 bg-blue-500/30" />

            <div className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 p-6 sm:p-8 mx-auto max-w-md">
              <div className="flex items-start justify-between mb-6">
                <div className="space-y-2">
                  <div className="h-2.5 w-16 rounded-full bg-blue-600" />
                  <div className="h-2 w-24 rounded-full bg-blue-200 dark:bg-blue-900" />
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-400">INV-0001</div>
                  <div className="font-heading font-bold text-lg text-slate-900 dark:text-slate-100 tracking-tight">INVOICE</div>
                </div>
              </div>

              <div className="space-y-2 mb-5">
                <div className="h-2 w-4/5 rounded-full bg-slate-100 dark:bg-slate-800" />
                <div className="h-2 w-3/5 rounded-full bg-slate-100 dark:bg-slate-800" />
              </div>

              <div className="rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden text-xs mb-4">
                <div className="grid grid-cols-4 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-500 font-medium">
                  <span>ITEM</span><span className="text-right">QTY</span><span className="text-right">PRICE</span><span className="text-right">AMOUNT</span>
                </div>
                {[[2, 100, 200], [1, 200, 200], [3, 50, 150]].map(([q, p, a], i) => (
                  <div key={i} className="grid grid-cols-4 px-3 py-2 border-t border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-300">
                    <span className="h-2 w-10 rounded-full bg-slate-200 dark:bg-slate-700 mt-1" />
                    <span className="text-right tabular-nums">{q}.00</span>
                    <span className="text-right tabular-nums">${p}.00</span>
                    <span className="text-right tabular-nums font-medium">${a}.00</span>
                  </div>
                ))}
              </div>

              <div className="rounded-xl bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest opacity-90">Grand Total</span>
                <span className="font-heading font-bold text-lg">$550.00</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
            Why Choose Our Invoice Generator?
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              data-testid={`feature-card-${i}`}
              className="group rounded-2xl bg-card border border-border p-8 text-center hover:-translate-y-1 hover:shadow-xl transition-all duration-300 hover:border-blue-200 dark:hover:border-blue-800"
            >
              <div className="w-14 h-14 mx-auto rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-600 grid place-items-center mb-5 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                <f.icon size={22} />
              </div>
              <h3 className="font-heading font-bold text-lg">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
